import {Component, Input, Output, EventEmitter} from '@angular/core';
import {AuthService} from '../../../../services/auth/auth.service';
import {CARD, CASH} from '../../sales-terminal.constants';
import {PaymentMethodEnum, PriceInfoEnum, PrintTicketType} from '../../../../generated/sales-terminal-client';
import {ValidationService} from '../../../../services/validation/validation.service';
import {UtilService} from '../../../../services/util/util.service';
import {CartAdvancedInterface} from '../../../../services/cart/cart.interface';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {NumericInputType} from '../../../../components/forms/numeric-input/numeric-input.component';
import {TextInputType} from '../../../../components/forms/text-input/text-input.component';

@Component({
  selector: 'app-cart-modals',
  templateUrl: './cart-modals.component.html',
  styleUrls: ['./cart-modals.component.scss']
})
export class CartModalsComponent<Type> {
  readonly CASH = CASH;
  readonly CARD = CARD;
  @Input() service: CartAdvancedInterface<Type>;
  @Input() modalType: string;
  @Input() paymentType: number;
  @Input() paymentMethod: PaymentMethodEnum;
  private _clientEmail: string;
  @Input()
  set clientEmail(value: string) {
    this._clientEmail = value;
    this.clientEmailChange.emit(this._clientEmail);
  }
  get clientEmail(): string {
    return this._clientEmail;
  }
  @Output() clientEmailChange: EventEmitter<string> = new EventEmitter<string>();
  @Input() ticketsPrintedCountBy: string;
  @Output() ticketsPrintedCountByChange: EventEmitter<string> = new EventEmitter<string>();
  @Input() backToSale: boolean;
  @Output() backToSaleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() ticketType: PrintTicketType;
  @Output() ticketTypeChange: EventEmitter<PrintTicketType> = new EventEmitter<PrintTicketType>();
  @Input() disabledButtons: boolean;
  @Output() checkoutCart: EventEmitter<any> = new EventEmitter<any>();
  @Output() cancelPayment: EventEmitter<any> = new EventEmitter<any>();
  @Output() proceedTransaction: EventEmitter<any> = new EventEmitter<any>();
  @Output() confirmPrinting: EventEmitter<any> = new EventEmitter<any>();
  @Output() scrappingProcess: EventEmitter<any> = new EventEmitter<any>();
  @Output() retryPrint: EventEmitter<any> = new EventEmitter<any>();

  private _internalComment: string;
  @Input()
  set internalComment(val: string) {
    this._internalComment = val;
    this.internalCommentChange.emit(this._internalComment);
  }
  get internalComment() {
    return this._internalComment;
  }
  @Output() internalCommentChange: EventEmitter<string> = new EventEmitter<string>();
  private _priceInfo: PriceInfoEnum;
  @Input()
  set priceInfo(val: PriceInfoEnum) {
    this._priceInfo = val;
    this.priceInfoChange.emit(this._priceInfo);
  }
  get priceInfo() {
    return this._priceInfo;
  }
  @Output() priceInfoChange: EventEmitter<PriceInfoEnum> = new EventEmitter<PriceInfoEnum>();

  public priceInfoEnum = PriceInfoEnum;
  public printTicketType = PrintTicketType;
  public cashAmount = 0;
  public Privileges = Privileges;

  constructor(public authService: AuthService,
              public utilService: UtilService) {}

  public get cashAmountToReturn(): number {
    return (((this.service.totalSum + this.service.cart?.total?.cashRounding || 0) / 100) - (+this.cashAmount || 0)) * 100;
  }

  public get canChooseTicketFormat(): boolean {
    return this.authService.checkPrivileges(Privileges.TICKET_TYPE_CHANGE) &&
           !this.authService.checkSalespointProperty('tickets_printed_in_pdf') &&
           !this.authService.checkSalespointProperty('fiscal_procedures_by');
  }

  public isValidEmail(email: string): boolean {
    return email && ValidationService.validateEmail(email);
  }
  public get isValidClientNotificationEmail(): boolean {
    return !this.service.clientNotificationEmail || ValidationService.validateEmail(this.service.clientNotificationEmail);
  }

  public isCashPaymentDisabled(): boolean {
    return (this.authService.checkSalespointProperty('cart_cashback') && !this.cashAmount)
      || !this.isValidClientNotificationEmail;
  }

  public closeModal(): void {
    switch (this.modalType) {
      case 'payment':
      case 'confirmRelease':
        this.cancelPayment.emit();
        break;
      case 'sendEmail':
        this.modalType = 'payment';
        break;
    }
  }

  public get isCash(): boolean {
    return this.paymentType === CASH || this.paymentMethod === PaymentMethodEnum.Cash;
  }
  public get isNoPayment(): boolean {
    return this.service.isSelfService && !this.paymentMethod;
  }
  public get showPromoterIssueMessage(): boolean {
    return this.isNoPayment && !this.authService.user.isSuperCentre && !this.authService.user.isSalespointUnderSuperCentre;
  }

  public get paymentModalTitle(): string {
    if (this.paymentType) {
      return this.isCash ? 'general.api_cart_cash' : 'general.api_cart_card';
    }
    return 'general.api_cart_' + (this.paymentMethod ? this.paymentMethod.toLowerCase() : 'no_payment');
  }

  public get showDetailCard(): boolean {
    return !this.utilService.isMobile || (this.modalType === 'payment' && this.service.isSelfService && this.isCash) || ['confirmRelease', 'confirmPrint', 'sendEmail'].includes(this.modalType);
  }

  protected readonly NumericInputType = NumericInputType;
  protected readonly TextInputType = TextInputType;
}
